import { FormValueScope, SelectableInputField } from '@epages/react-components'
import type { Ref } from 'react'
import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { kebabCase } from 'lodash-es'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'
import cc from 'classcat'

import type { DefaultValueParams } from './FooterSettings'
import type { DraggableRef } from '../../utils/hooks/useSortableHelpers'
import { Popover } from '../Popover'
import { Tooltip } from '../Tooltip'
import { handleEscapeKeyDown } from '../../utils/hooks/useSortableHelpers'
import { useTranslate } from '../../utils/translate'
import FooterContactSettings from './FooterContactSettings'
import FooterPagesPopover from './FooterPagesPopover'
import FormGroup from '../formComponents/FormGroup'
import FormValue from '../formComponents/FormValue'
import LegalPagesPopover from './LegalPagesPopover'
import PaymentSettings from './PaymentSettings'
import ShippingProviderSettings from './ShippingProviderSettings'
import SocialMediaPopover from './SocialMediaPopover'

const popovers = {
  footerPages: FooterPagesPopover,
  legalPages: LegalPagesPopover,
  payments: PaymentSettings,
  shipping: ShippingProviderSettings,
  socialMedia: SocialMediaPopover,
  contact: FooterContactSettings,
} as Record<string, any>

FooterSectionsSettings.defaultValue = (params: DefaultValueParams) => {
  const { userSettings, shopLocale, footerPages, shopResellerName, taxCountry, t } = params
  return {
    footerPages: Immutable.List(),
    legalPages: Immutable.List(),
    paymentLogos: PaymentSettings.defaultValue({ shopResellerName, taxCountry }),
    shippingLogos: ShippingProviderSettings.defaultValue({ shopLocale }),
    socialNetworks: SocialMediaPopover.defaultValue,
    contact: FooterContactSettings.defaultValue,
    footerLayout: Immutable.fromJS({
      footerSections: {
        legalPages: {
          isVisible: true,
          title: t('sections.legalPages.defaultTitle'),
          titleSelected: false,
        },
        footerPages: {
          isVisible: footerPages?.size,
          title: t('sections.footerPages.defaultTitle'),
          titleSelected: false,
        },
        payments: {
          isVisible: userSettings?.get('payments')?.size,
          title: t('sections.payments.defaultTitle'),
          titleSelected: false,
        },
        shipping: {
          isVisible: userSettings?.get('shipping')?.size,
          title: t('sections.shipping.defaultTitle'),
          titleSelected: false,
        },
        socialMedia: {
          isVisible: true,
          title: t('sections.socialMedia.defaultTitle'),
          titleSelected: false,
        },
        contact: {
          isVisible: false,
          title: t('sections.contact.defaultTitle'),
          titleSelected: false,
        },
      },
    }),
  }
}

export default function FooterSectionsSettings({ showSocialMedia = false }: Readonly<{ showSocialMedia?: boolean }>) {
  const [activeSection, setActiveSection] = useState<keyof typeof popovers | null>(null)
  const t = useTranslate('interface', 'components.bottomBar.footerSettings.sections')
  const layout = useSelector<State, 'row' | 'column'>((state) =>
    state.getIn(['shop', 'userSettings', 'footerLayout', 'variant']),
  )
  const hasFooterPages = useSelector<State, boolean>((state) => state.get('footerPages')?.size > 0)

  const ref = useRef<HTMLDivElement>(null)

  const handleFocus = () => {
    ref.current?.focus({ preventScroll: true })
  }

  const socialMediaButtonRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    if (showSocialMedia) socialMediaButtonRef.current?.click()
  }, [showSocialMedia])

  return (
    <div className="ep-footer-sections-settings" ref={ref} tabIndex={-1}>
      <div className="dali-settingslayer-element small">{t(`description.${layout}Layout`)}</div>
      <div className="dali-settingslayer-element">
        {layout === 'column' && <h3>{t('columnTitle', { number: 1 })}</h3>}
        <Section
          name={'legalPages'}
          isActive={activeSection === 'legalPages'}
          onActivate={setActiveSection}
          onFocus={handleFocus}
          t={t}
        />

        {layout === 'column' && <h3>{t('columnTitle', { number: 2 })}</h3>}
        <Section
          name={'footerPages'}
          isActive={activeSection === 'footerPages'}
          onActivate={setActiveSection}
          onFocus={handleFocus}
          t={t}
          hasFooterPages={hasFooterPages}
        />

        {layout === 'column' && <h3>{t('columnTitle', { number: 3 })}</h3>}
        <Section
          name={'payments'}
          isActive={activeSection === 'payments'}
          onActivate={setActiveSection}
          onFocus={handleFocus}
          t={t}
        />
        <Section
          name={'shipping'}
          isActive={activeSection === 'shipping'}
          onActivate={setActiveSection}
          onFocus={handleFocus}
          t={t}
        />

        {layout === 'column' && <h3>{t('columnTitle', { number: 4 })}</h3>}
        <Section
          name={'socialMedia'}
          buttonRef={socialMediaButtonRef}
          isActive={activeSection === 'socialMedia'}
          onActivate={setActiveSection}
          onFocus={handleFocus}
          t={t}
        />
        <Section
          name={'contact'}
          isActive={activeSection === 'contact'}
          onActivate={setActiveSection}
          onFocus={handleFocus}
          t={t}
        />
      </div>
    </div>
  )
}

function Section({ t, name, buttonRef, isActive, onActivate, onFocus, hasFooterPages }: SectionProps) {
  const layout = useSelector<State, 'row' | 'column'>((state) =>
    state.getIn(['shop', 'userSettings', 'footerLayout', 'variant']),
  )
  const footerSections = useSelector<State, Immutable.Map<string, boolean>>((state) =>
    state.getIn(['shop', 'userSettings', 'footerLayout', 'footerSections']),
  )
  const isHiddenSection =
    name === 'contact'
      ? footerSections?.getIn([name, 'isVisible']) !== true
      : footerSections?.getIn([name, 'isVisible']) === false
  const ariaLabelProps = hasFooterPages === false ? { 'aria-label': t('footerPages.tooltip') } : {}

  const popoverRef = useRef<HTMLDivElement>(null)
  const fieldsetRef = useRef<HTMLFieldSetElement>(null)
  const draggableRef = useRef<DraggableRef>(null)

  const closePopover = useCallback(
    (event) => {
      if (!isActive || draggableRef.current?.isDragging()) return

      // allow left click on cancel button
      if (event.buttons < 2 && (event.target as HTMLElement).classList.contains('ep-modal-button-cancel')) {
        onActivate(null)
        return
      }

      // check for invalid elements inside the popover
      for (const element of Array.from(fieldsetRef.current?.elements || []) as HTMLInputElement[]) {
        if (!element.checkValidity()) {
          element.scrollIntoView({ behavior: 'instant', block: 'start' })
          element.reportValidity()
          event.preventDefault()
          event.stopPropagation()
          // keep the popover open
          return
        }
      }

      // else proceed with closing
      onActivate(null)
      onFocus()
    },
    [isActive, onActivate, onFocus],
  )

  useEffect(() => {
    if (!isActive) return

    const handleOutsideClick = (event: MouseEvent) => {
      const popoverElement = (event.target as Element)?.closest('.ep-footer-sections-settings-popover')
      if (!popoverElement && !fieldsetRef.current?.contains(event.target as Node)) {
        closePopover(event)
      }
    }

    document.addEventListener('click', handleOutsideClick, { capture: true })

    return () => {
      document.removeEventListener('click', handleOutsideClick, { capture: true })
    }
  }, [closePopover, isActive])

  const sectionDiv = (
    <div
      ref={popoverRef}
      {...ariaLabelProps}
      className={cc([
        'ep-footer-sections-settings-section',
        { 'ep-footer-sections-settings-no-footer-pages': hasFooterPages === false },
      ])}
    >
      <span
        className={cc([
          `ep-footer-sections-settings-icon-${kebabCase(name)}`,
          { 'ep-footer-sections-settings-hide-section': isHiddenSection },
        ])}
      />
      <span
        className={cc([
          'ep-footer-sections-settings-text',
          { 'ep-footer-sections-settings-hide-section': isHiddenSection },
        ])}
      >
        {t(`${name}.defaultTitle`)}
      </span>
      <button
        ref={buttonRef}
        className="ep-footer-sections-settings-icon-gearwheel"
        aria-label={t('editButton.tooltip')}
        title={t('editButton.tooltip')}
        type="button"
        onClick={() => {
          // prevent activating this popover if no footer pages
          if (name !== 'footerPages' || hasFooterPages !== false) {
            onActivate(name)
          }
        }}
      />
    </div>
  )

  return (
    <>
      {hasFooterPages === false ? (
        <div className="ep-footer-sections-settings-tooltip">
          <Tooltip sideOffset={10} hideIcon trigger={sectionDiv} content={t('footerPages.tooltip')} />
        </div>
      ) : (
        sectionDiv
      )}
      <Popover
        open={isActive}
        side="right"
        sideOffset={30}
        className="ep-footer-sections-settings-popover"
        onEscapeKeyDown={handleEscapeKeyDown(closePopover)}
        avoidCollisions
        keepOpenOnClick
        usePortal
        virtualRef={popoverRef}
      >
        <>
          <HideSectionToggle t={t} activePopover={name} />
          {layout === 'column' && <SectionOptionalHeadline t={t} activePopover={name} />}
          {createElement(popovers[name], { draggableRef, fieldsetRef })}
        </>
      </Popover>
    </>
  )
}

function HideSectionToggle({ t, activePopover }: Readonly<TranslateProps & { activePopover: keyof typeof popovers }>) {
  return (
    <FormValueScope name={`footerLayout.footerSections.${activePopover}`}>
      <>
        <FormGroup
          name="isVisible"
          className="ep-form-row"
          label={t('popovers.hideSection.label')}
          tagName="label"
          labelAfterComponent
        >
          <FormValue name="isVisible">
            {(value, setValue) => (
              <div className="ep-flip-switch">
                <input
                  type="checkbox"
                  checked={value === false}
                  onChange={() => setValue(!(value ?? true))}
                  className="ep-flip-switch-checkbox"
                />
                <span className="ep-flip-switch-label"></span>
              </div>
            )}
          </FormValue>
        </FormGroup>
        <p className="ep-form-row">{t('popovers.description')}</p>
      </>
    </FormValueScope>
  )
}

function SectionOptionalHeadline({
  t,
  activePopover,
}: Readonly<TranslateProps & { activePopover: keyof typeof popovers }>) {
  return (
    <div className="ep-form-row ep-footer-sections-settings-popover-headline">
      <FormValueScope name={`footerLayout.footerSections.${activePopover}`}>
        <SelectableInputField
          type="text"
          name="title"
          label={t(`popovers.sectionHeadline.title`)}
          aria-label={t(`popovers.sectionHeadline.title`)}
        />
      </FormValueScope>
    </div>
  )
}

type SectionProps = Readonly<{
  name: keyof typeof popovers
  buttonRef?: Ref<HTMLButtonElement>
  isActive: boolean
  onActivate: (section: keyof typeof popovers | null) => void
  onFocus: () => void
  hasFooterPages?: boolean
}> &
  TranslateProps
